import { FC } from "react";
import { 
    FaLink, 
    FileField, 
    InputField, 
    RequiredNote, 
    UPLOAD_MAX_DOC_FILE_SIZE, 
    FileList 
} from "@/Shared";
import { DocumentFormValues } from "../../Logic";

interface UploadDocFormProps {
    values: DocumentFormValues
}

/**
 * Component: UploadDocForm
 *
 * Description:
 * This component handles document creation when the document
 * is NOT linked to an existing business resource.
 *
 * It allows the user to:
 * - Provide an optional external source URL
 * - Upload a physical document file
 * - Preview the selected file before submission
 *
 * This form represents the "standalone document" use case,
 * typically used when the document source is unknown or external.
 *
 * Responsibilities:
 * - Collect file-related inputs
 * - Display basic upload feedback
 * - Enforce required upload constraints
 *
 * Design principles:
 * - Focused on file input concerns only
 * - Stateless UI driven by Formik values
 * - No routing or permission logic
 *
 * Author: Samano CASTRE
 */
const UploadDocForm: FC<UploadDocFormProps> = ({ values }) => {

    /**
	 * Renders the standalone document upload section.
	 *
	 * Displays:
	 * - An optional URL source field
	 * - A required file upload field
	 * - A preview of the selected file (if any)
	 *
	 * Validation and submission are handled upstream by the main form.
	 */
    return (
        <fieldset className='width-30'>
            <legend>Document</legend>
            <InputField
                label='URL Source'
                icon={<FaLink />}
                name='documentSource'
                placeholder="Saisir l'URL de la source"
                autoComplete='on'
                type='url'
            />
            <FileField
                required
                label='Fichier'
                name='uploadedFile'
                placeholder={`Sélectionner un fichier (${UPLOAD_MAX_DOC_FILE_SIZE}Mo Max.)`}
            />
            {
                values.uploadedFile?.name 
                && <FileList  uploadedFile={[{ file: values.uploadedFile }]} />
            }

            <RequiredNote />
        </fieldset>
    )
}

export default UploadDocForm;